import { Step, StepButton, StepLabel, Stepper, StepperProps, StepProps } from '@mui/material';
import { StepIndexType, StepLabelType } from '@shared/types';
import { FC } from 'react';

interface MultiStepDialogStepperProps {
    steps: StepLabelType[];
    activeStepIndex: StepIndexType;
    onStepClick?: (step: StepIndexType) => () => void;
    nonLinear?: boolean;
    isStepSkipped?: (step: StepIndexType) => boolean;
    labelFontSize?: string | number;
    iconSize?: number;
    stepperProps?: Omit<StepperProps, 'activeStep' | 'children' | 'nonLinear'>;
}

const MultiStepDialogStepper: FC<MultiStepDialogStepperProps> = ({
    steps,
    activeStepIndex,
    onStepClick,
    nonLinear = false,
    isStepSkipped,
    labelFontSize = '0.87rem',
    iconSize = 24,
    stepperProps,
}) => {
    const defaultStepCompleted = (index: number, activeStepIndex: StepIndexType) => {
        return nonLinear ? undefined : index < activeStepIndex;
    };

    return (
        <Stepper
            nonLinear={nonLinear}
            activeStep={activeStepIndex}
            sx={{
                fontSize: labelFontSize,
                fontWeight: 600,
                ...stepperProps?.sx,
            }}
            {...stepperProps}
        >
            {steps.map((label, index) => {
                const stepProps: StepProps = {};

                if (isStepSkipped?.(index)) {
                    stepProps.completed = false;
                } else {
                    stepProps.completed = defaultStepCompleted(index, activeStepIndex);
                }

                return (
                    <Step key={`${label}-${index}`} {...stepProps}>
                        <StepButton
                            onClick={onStepClick?.(index)}
                            disabled={!nonLinear && !onStepClick}
                            sx={{
                                height: 'fit-content',
                                pt: 2,
                                pb: 2,
                                '.MuiStepLabel-label': {
                                    fontSize: labelFontSize,
                                    fontWeight: 600,
                                },
                                '.MuiStepLabel-iconContainer svg': {
                                    width: `${iconSize}px`,
                                    height: `${iconSize}px`,
                                },
                                '.MuiStepLabel-iconContainer svg text': {
                                    fontSize: labelFontSize,
                                    fontWeight: 600,
                                },
                            }}
                        >
                            <StepLabel
                                slotProps={{
                                    label: {
                                        sx: {
                                            fontSize: labelFontSize,
                                            fontWeight: 600,
                                        },
                                    },
                                    stepIcon: {
                                        sx: {
                                            fontSize: iconSize,
                                            text: { fontSize: labelFontSize },
                                        },
                                    },
                                }}
                            >
                                {label}
                            </StepLabel>
                        </StepButton>
                    </Step>
                );
            })}
        </Stepper>
    );
};

export default MultiStepDialogStepper;
